import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames/bind';
import Loader from 'yoda-core-components/lib/components/Loader/Loader';
import { getChallenges, acceptChallenge } from '../../actions/ChallengesAction';
import {
    selectShowLoader,
    selectTier,
    selectOfferSelection,
    selectIsEligibleTier,
    selectTierLimit,
    selectInProgressChallenges,
    selectNotStartedChallenges,
    selectCompletedChallenges,
    selectExpiredChallenges,
    selectTabCounts,
    makeSelectUrgencyMessage,
} from '../../selectors/NewChallengesSelectors';
import { getChallengeTabMessage } from '../../helpers/getChallengeTabMessage';
import ChallengeHeader from './ChallengeHeader';
import TierProgress from './TierProgress';
import ResponsiveTabNav from '../ResponsiveTabNav/ResponsiveTabNav';
import TabMessage from './TabMessage';
import ChallengeCardInProgress from './ChallengeCardInProgress';
import ChallengeCardNotStarted from './ChallengeCardNotStarted';
import ChallengeCardCompleted from './ChallengeCardCompleted';
import ExclusionsSlidePanel from './ExclusionsSlidePanel';
import * as styles from './NewChallenges.css';

const cx = classNames.bind(styles);

const NewChallenges = () => {
    const dispatch = useDispatch();

    // Redux state
    const showLoader = useSelector(selectShowLoader);
    const tier = useSelector(selectTier);
    const offerSelection = useSelector(selectOfferSelection);
    const isEligible = useSelector(selectIsEligibleTier);
    const tierLimit = useSelector(selectTierLimit);
    const inProgressChallenges = useSelector(selectInProgressChallenges);
    const notStartedChallenges = useSelector(selectNotStartedChallenges);
    const completedChallenges = useSelector(selectCompletedChallenges);
    const expiredChallenges = useSelector(selectExpiredChallenges);
    const tabCounts = useSelector(selectTabCounts);
    const error = useSelector((state) => state.challengesDetails?.error);
    const enableExpiredChallenges = useSelector(
        (state) => state.context?.featureFlags?.enableExpiredChallenges || false
    );

    // Local state
    const [activeTab, setActiveTab] = useState('inProgress');
    const [showExclusionsPanel, setShowExclusionsPanel] = useState(false);
    const [selectedChallenge, setSelectedChallenge] = useState(null);
    const [isMdPlus] = useState(() => typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches);

    // Memoized urgency message selector factory
    const selectUrgencyMessage = useMemo(() => makeSelectUrgencyMessage(), []);

    // Fetch challenges on mount
    useEffect(() => {
        dispatch(getChallenges());
    }, [dispatch]);

    // Tab configuration
    const tabs = [
        { label: 'In Progress', value: 'inProgress', count: tabCounts.inProgress },
        { label: 'Not Started', value: 'notStarted', count: tabCounts.notStarted },
        { label: 'Completed', value: 'completed', count: tabCounts.completed },
        ...(enableExpiredChallenges
            ? [{ label: 'Expired', value: 'expired', count: tabCounts.expired }]
            : []),
    ];

    // Tab message computation
    const tabMessage = useMemo(() => {
        const state = {
            completed: offerSelection.completed || 0,
            active: offerSelection.active || 0,
            limit: tierLimit,
            inProgressCount: tabCounts.inProgress,
            notStartedCount: tabCounts.notStarted,
            selectionAllowed: offerSelection.selectionAllowed !== false,
        };
        return getChallengeTabMessage(activeTab, tier, state);
    }, [activeTab, tier, offerSelection, tierLimit, tabCounts]);

    // Handlers
    const handleTabChange = (value) => {
        setActiveTab(value);
    };

    const handleAcceptChallenge = (offerId) => {
        dispatch(acceptChallenge(offerId));
    };

    const handleExclusionsClick = (challenge) => {
        setSelectedChallenge(challenge);
        setShowExclusionsPanel(true);
    };

    const handleCloseExclusions = () => {
        setShowExclusionsPanel(false);
        setSelectedChallenge(null);
    };

    const handleRetry = () => {
        dispatch(getChallenges());
    };

    // Get the filtered challenges for the active tab
    const getActiveChallenges = () => {
        switch (activeTab) {
            case 'inProgress':
                return inProgressChallenges;
            case 'notStarted': {
                const { active = 0, completed: completedCount = 0 } = offerSelection;
                if (tierLimit > 0 && active + completedCount >= tierLimit) return [];
                return notStartedChallenges;
            }
            case 'completed':
                return completedChallenges;
            case 'expired':
                return expiredChallenges;
            default:
                return [];
        }
    };

    // Render challenge cards based on active tab
    const renderChallengeCards = () => {
        const challenges = getActiveChallenges();

        if (!challenges || challenges.length === 0) {
            return null;
        }

        return (
            <div className={cx('challengeList')}>
                {challenges.map((challenge) => {
                    switch (activeTab) {
                        case 'inProgress': {
                            const urgencyMessage = selectUrgencyMessage(null, challenge);
                            return (
                                <ChallengeCardInProgress
                                    key={challenge.offerId}
                                    challenge={challenge}
                                    urgencyMessage={urgencyMessage}
                                    onExclusionsClick={handleExclusionsClick}
                                />
                            );
                        }
                        case 'notStarted':
                            return (
                                <ChallengeCardNotStarted
                                    key={challenge.offerId}
                                    challenge={challenge}
                                    onAccept={handleAcceptChallenge}
                                    isAccepting={showLoader}
                                    onExclusionsClick={handleExclusionsClick}
                                />
                            );
                        case 'completed':
                            return (
                                <ChallengeCardCompleted
                                    key={challenge.offerId}
                                    challenge={challenge}
                                    onDetailsClick={handleExclusionsClick}
                                />
                            );
                        case 'expired':
                            return (
                                <ChallengeCardInProgress
                                    key={challenge.offerId}
                                    challenge={challenge}
                                    onExclusionsClick={handleExclusionsClick}
                                    progressBarVariant="grey"
                                />
                            );
                        default:
                            return null;
                    }
                })}
            </div>
        );
    };

    // Error state
    if (error && !showLoader) {
        return (
            <div className={cx('newChallengesContainer')}>
                <ChallengeHeader />
                <div className={cx('errorContainer')}>
                    <p className={cx('errorMessage')}>
                        Something went wrong loading your challenges.
                    </p>
                    <button
                        type="button"
                        className={cx('retryButton')}
                        onClick={handleRetry}
                        data-automation-id="challenges-retry-btn"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }
    return (
        <div className={cx('newChallengesContainer')} data-automation-id="new-challenges-container">
            {showLoader && (
                <div className={cx('loaderWrapper')}>
                    <Loader keepOverlay automationId="new-challenges-loader" />
                </div>
            )}

            <div className={cx('headerSection')}>
                <ChallengeHeader />

                <TierProgress
                    tier={tier}
                    completed={offerSelection.completed || 0}
                    limit={tierLimit}
                />
            </div>

            <ResponsiveTabNav
                tabs={tabs}
                activeTab={activeTab}
                onTabChange={handleTabChange}
                hidden={!isEligible}
                ariaLabel="Challenge categories"
                className={cx('tabNav')}
                showTabs={isMdPlus}
            />

            <TabMessage message={tabMessage} />

            {!showLoader && renderChallengeCards()}

            <ExclusionsSlidePanel
                isOpen={showExclusionsPanel}
                challenge={selectedChallenge}
                onClose={handleCloseExclusions}
            />
        </div>
    );
};

export default NewChallenges;
